/**
 * Lander General fields: name, tags, URL, tokens, type, notes (Voluum order).
 * For Media Devoted | Created By Ciro Autiero
 */

import { Input } from '@/components/ui/form/input/Input';
import { cn } from '@/lib/utils';

import { LanderTagsField } from '../tag-field';
import { VoluumUrlTokensPanel } from '../tokens';

import type { FC } from 'react';
import type { VoluumLanderFormValues } from '../map-voluum-lander-form';

const labelCls = 'text-xs font-semibold text-muted-foreground';
const fieldCls = 'space-y-1';

export interface LanderGeneralIdentitySectionProps {
  values: VoluumLanderFormValues;
  patch: (p: Partial<VoluumLanderFormValues>) => void;
  disabled?: boolean;
}

const LanderGeneralIdentitySection: FC<LanderGeneralIdentitySectionProps> = ({
  values,
  patch,
  disabled,
}) => (
  <div className="space-y-3 text-sm">
    <div className={fieldCls}>
      <label className={labelCls}>Full name (Voluum)</label>
      <Input
        value={values.fullNameReadOnly}
        readOnly
        className="h-8 bg-muted/40 text-muted-foreground"
        aria-readonly
      />
    </div>
    <div className={fieldCls}>
      <label className={labelCls} htmlFor="lander-name-postfix">
        Name postfix
      </label>
      <Input
        id="lander-name-postfix"
        value={values.namePostfix}
        disabled={disabled}
        onChange={(e) => patch({ namePostfix: e.target.value })}
        className="h-8"
      />
    </div>
    <div className={fieldCls}>
      <label className={labelCls} htmlFor="lander-tags">
        Tags
      </label>
      <LanderTagsField
        tagsCsv={values.tagsCsv}
        onTagsCsvChange={(csv) => patch({ tagsCsv: csv })}
        disabled={disabled}
      />
    </div>
    <div className={fieldCls}>
      <label className={labelCls} htmlFor="lander-url">
        Lander URL
      </label>
      <Input
        id="lander-url"
        value={values.url}
        disabled={disabled}
        onChange={(e) => patch({ url: e.target.value })}
        className="h-8 font-mono text-xs"
      />
      <VoluumUrlTokensPanel
        url={values.url}
        disabled={disabled}
        onUrlChange={(nextUrl) => patch({ url: nextUrl })}
      />
    </div>
    <div className={fieldCls}>
      <label className={labelCls} htmlFor="lander-type">
        Type
      </label>
      <select
        id="lander-type"
        disabled={disabled}
        value={values.landerType}
        onChange={(e) =>
          patch({
            landerType:
              e.target.value === 'PRELANDER' ? 'PRELANDER' : 'LANDER',
          })
        }
        className={cn(
          'h-8 w-full rounded-md border border-input bg-background px-2 text-sm',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <option value="LANDER">Lander</option>
        <option value="PRELANDER">Pre-lander</option>
      </select>
    </div>
    <div className={fieldCls}>
      <label className={labelCls} htmlFor="lander-note">
        Notes
      </label>
      <textarea
        id="lander-note"
        disabled={disabled}
        value={values.noteText}
        onChange={(e) => patch({ noteText: e.target.value })}
        rows={3}
        className="w-full resize-y rounded-md border border-input bg-transparent px-2 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
      />
    </div>
  </div>
);

export default LanderGeneralIdentitySection;
